/* eslint-disable no-return-assign */
/* eslint-disable no-lonely-if */
import * as Yup from 'yup';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import { Stack, IconButton, InputAdornment, Box, Typography, Divider, Button, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';

// auth action
// Note: Ensure your actual actions are imported correctly here
import { loginUser, loginWithGoogle, sendOTP, loginWithOTP } from '../../../store/actions/auth';
import { apiMain } from '../../../api/config/axios';

// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// --- Styled Components ---

const AppleTextField = styled(RHFTextField)({
  '& .MuiOutlinedInput-root': {
    fontFamily: '"SF Pro Text", -apple-system, sans-serif',
    borderRadius: 12,
    background: '#f5f5f7',
    border: '1.5px solid #d2d2d7',
    '& fieldset': { border: '1.5px solid transparent' },
    '&.Mui-focused': {
      background: '#ffffff',
      boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.12)',
      '& fieldset': { borderColor: '#0071e3' },
    },
  },
});

const SubmitButton = styled(LoadingButton)(({ theme, disabled }) => ({
  borderRadius: 12,
  height: 50,
  textTransform: 'none',
  color: 'white',
  fontWeight: 590,
  background: disabled ? '#bcbcc5' : 'linear-gradient(180deg, #1c1c1e 0%, #000000 100%)',
  '&:hover': { background: disabled ? '#bcbcc5' : '#2c2c2e' },
  boxShadow: 'none',
  '&.Mui-disabled': {
    color: 'white',
    background: '#bcbcc5',
  },
}));

const SocialButton = styled(Button)({
  borderRadius: 12,
  height: 50,
  textTransform: 'none',
  fontWeight: 500,
  border: '1.5px solid #d2d2d7',
  color: '#1d1d1f',
  backgroundColor: '#fff',
  '&:hover': { backgroundColor: '#f5f5f7', borderColor: '#d2d2d7' },
});

// --- Custom Components ---

const OTPInput = ({ name }) => {
  const { control } = useFormContext();
  const inputRefs = useRef([]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const otpValues = (value || '').split('').concat(Array(6).fill('')).slice(0, 6);

        const handleChange = (e, index) => {
          const val = e.target.value;
          if (/[^0-9]/.test(val)) return;

          const newOtp = [...otpValues];
          newOtp[index] = val.substring(val.length - 1);

          onChange(newOtp.join(''));

          if (val && index < 5) {
            inputRefs.current[index + 1]?.focus();
          }
        };

        const handleKeyDown = (e, index) => {
          if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
          }
        };

        const handlePaste = (e) => {
          e.preventDefault();
          const pasteData = e.clipboardData
            .getData('text')
            .replace(/[^0-9]/g, '')
            .substring(0, 6);
          if (pasteData) {
            onChange(pasteData);
            const focusIndex = Math.min(pasteData.length, 5);
            if (inputRefs.current[focusIndex]) {
              inputRefs.current[focusIndex].focus();
            } else if (inputRefs.current[5]) {
              inputRefs.current[5].focus();
            }
          }
        };

        return (
          <Box>
            <Typography sx={{ mb: 1, color: '#6e6e73', fontWeight: 500, fontSize: '0.875rem' }}>OTP</Typography>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              {otpValues.map((digit, index) => (
                <Box
                  key={index}
                  component="input"
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  sx={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '6px',
                    border: '1.5px solid #d2d2d7',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontFamily: '"SF Pro Text", -apple-system, sans-serif',
                    background: '#ffffff',
                    outline: 'none',
                    '&:focus': {
                      borderColor: '#0071e3',
                      boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.12)',
                    },
                  }}
                />
              ))}
            </Stack>
            {error && <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>{error.message}</Typography>}
          </Box>
        );
      }}
    />
  );
};

// ----------------------------------------------------------------------

export default function LoginForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loginMethod, setLoginMethod] = useState('otp'); // Default to OTP/Email flow
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(36);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState('');
  const isTurnstileEnabled = process.env.REACT_APP_TURNSTILE_ENABLED === 'true';

  const turnstileRef = useCallback((node) => {
    if (node !== null && isTurnstileEnabled && failedAttempts >= 3) {
      node.innerHTML = '';
      
      const renderWidget = () => {
        if (window.turnstile) {
          try {
            window.turnstile.render(node, {
              sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA',
              callback: (token) => {
                setTurnstileToken(token);
              },
              'error-callback': () => {
                setTurnstileToken('');
              },
              'expired-callback': () => {
                setTurnstileToken('');
              }
            });
          } catch (e) {
            console.error('Turnstile render error', e);
          }
        }
      };

      if (window.turnstile) {
        renderWidget();
      } else {
        const scriptId = 'cloudflare-turnstile-script';
        let script = document.getElementById(scriptId);
        if (!script) {
          script = document.createElement('script');
          script.id = scriptId;
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
          script.async = true;
          script.defer = true;
          document.body.appendChild(script);
        }
        window.onloadTurnstileCallback = renderWidget;
      }
    }
  }, [failedAttempts]);

  const { isLoading, isAuthenticated } = useSelector((state) => state.auth);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Must be a valid email').required('Email is required'),
    password: loginMethod === 'password' ? Yup.string().required('Password is required') : Yup.string(),
    otp:
      loginMethod === 'otp' && otpSent
        ? Yup.string().length(6, 'OTP must be 6 digits').required('OTP is required')
        : Yup.string(),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: { email: '', password: '', otp: '', remember: true },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = methods;

  const currentEmail = watch('email');
  const currentOtp = watch('otp');

  useEffect(() => {
    if (!currentEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentEmail)) {
      setFailedAttempts(0);
      setTurnstileToken('');
      return;
    }

    const delayDebounce = setTimeout(async () => {
      try {
        const response = await apiMain.get(`/api/v1/user/captcha-check?email=${encodeURIComponent(currentEmail)}`);
        if (response && response.data && response.data.result) {
          const { captchaRequired, loginAttempts } = response.data.result;
          if (captchaRequired) {
            setFailedAttempts(loginAttempts || 3);
          } else {
            setFailedAttempts(loginAttempts || 0);
          }
        }
      } catch (err) {
        console.error('Failed to check captcha status:', err);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [currentEmail]);

  const onSubmit = async (data) => {
    const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
    const appId = bootstrappedApp ? JSON.parse(bootstrappedApp)._id : undefined;

    const payload = {
      ...data,
      appId,
      captchaToken: (isTurnstileEnabled && failedAttempts >= 3) ? turnstileToken : undefined,
    };

    if (loginMethod === 'password') {
      const res = await dispatch(loginUser(payload));
      if (res && !res.success) {
        if (res.captchaRequired || res.loginAttempts >= 3) {
          setFailedAttempts(res.loginAttempts || 3);
        } else {
          setFailedAttempts((prev) => prev + 1);
        }
        if (window.turnstile) {
          window.turnstile.reset();
        }
        setTurnstileToken('');
      }
    } else {
      if (!otpSent) {
        const success = await dispatch(sendOTP(data.email));
        if (success) {
          setOtpSent(true);
          setCountdown(36);
        }
      } else {
        const res = await dispatch(loginWithOTP({
          email: data.email,
          otp: data.otp,
          appId,
          captchaToken: failedAttempts >= 3 ? turnstileToken : undefined
        }));
        if (res && !res.success) {
          if (res.captchaRequired || res.loginAttempts >= 3) {
            setFailedAttempts(res.loginAttempts || 3);
          } else {
            setFailedAttempts((prev) => prev + 1);
          }
          if (window.turnstile) {
            window.turnstile.reset();
          }
          setTurnstileToken('');
        }
      }
    }
  };

  const handleGoogleLogin = () => {
    const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
    const appId = bootstrappedApp ? JSON.parse(bootstrappedApp)._id : undefined;

    if (window.google) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID',
        scope: 'email profile',
        callback: (response) => {
          if (response && response.access_token) {
            dispatch(loginWithGoogle(response.access_token, appId));
          }
        },
      });
      client.requestAccessToken();
    } else {
      console.error('Google Identity Services script not loaded');
    }
  };

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let timer;
    if (loginMethod === 'otp' && otpSent && countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [loginMethod, otpSent, countdown]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {loginMethod === 'otp' && otpSent ? (
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ color: '#6e6e73', fontSize: '0.9rem', mb: 0.5 }}>
              To complete your account set up, enter the code sent to:
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ color: '#1d1d1f', fontWeight: 600, fontSize: '0.9rem' }}>{currentEmail}</Typography>
              <IconButton size="small" onClick={() => setOtpSent(false)} sx={{ color: '#6e6e73' }}>
                <Iconify icon="eva:edit-2-outline" width={16} />
              </IconButton>
            </Stack>
          </Box>

          <OTPInput name="otp" />

          <Typography sx={{ textAlign: 'center', color: '#6e6e73', fontSize: '0.875rem' }}>
            {countdown > 0 ? (
              `Resend OTP in ${countdown} seconds`
            ) : (
              <Link
                component="button"
                variant="body2"
                onClick={async (e) => {
                  e.preventDefault();
                  const success = await dispatch(sendOTP(currentEmail));
                  if (success) setCountdown(36);
                }}
                sx={{ color: '#0071e3', textDecoration: 'none' }}
              >
                Resend OTP
              </Link>
            )}
          </Typography>

          {isTurnstileEnabled && failedAttempts >= 3 && (
            <Box sx={{ my: 1, display: 'flex', justifyContent: 'center' }}>
              <div ref={turnstileRef} />
            </Box>
          )}

          <SubmitButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            disabled={(!currentOtp || currentOtp.length !== 6) || (isTurnstileEnabled && failedAttempts >= 3 && !turnstileToken)}
            loading={isSubmitting || isLoading}
          >
            Sign in
          </SubmitButton>

          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mt: 2 }}>
            <Link
              component="button"
              onClick={(e) => {
                e.preventDefault();
                setLoginMethod('password');
                setOtpSent(false);
                setFailedAttempts(0);
                setTurnstileToken('');
              }}
              sx={{
                color: '#0071e3',
                fontSize: '0.875rem',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in with password
            </Link>
            <Typography sx={{ color: '#d2d2d7' }}>|</Typography>
            <Link
              component="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/register');
              }}
              sx={{
                color: '#0071e3',
                fontSize: '0.875rem',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Create an account
            </Link>
          </Stack>

          <Typography sx={{ textAlign: 'left', color: '#86868b', fontSize: '0.75rem', mt: 4, lineHeight: 1.5 }}>
            By using our product, you agree to our <strong>Terms & Conditions</strong> & <strong>Privacy Policy</strong>
            . You also agree to receiving occasional emails about our products and services on your registered email
            address.
          </Typography>
        </Stack>
      ) : (
        <>
          <Stack spacing={2.5}>
            <AppleTextField name="email" label="Email Address" disabled={otpSent} />

            {loginMethod === 'password' && (
              <>
                <AppleTextField
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Stack direction="row" alignItems="center" justifyContent="flex-end">
                  <Link
                    variant="subtitle2"
                    underline="hover"
                    sx={{ cursor: 'pointer', color: '#0071e3' }}
                    onClick={() => navigate('/password-reset')}
                  >
                    Forgot password?
                  </Link>
                </Stack>
              </>
            )}
          </Stack>

          {isTurnstileEnabled && failedAttempts >= 3 && (
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
              <div ref={turnstileRef} />
            </Box>
          )}

          <SubmitButton
            sx={{ marginTop: '16px' }}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            disabled={isTurnstileEnabled && failedAttempts >= 3 && !turnstileToken}
            loading={isSubmitting || isLoading}
          >
            {loginMethod === 'otp' && !otpSent ? 'Continue' : 'Sign in'}
          </SubmitButton>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              OR
            </Typography>
          </Divider>

          <SocialButton
            fullWidth
            variant="outlined"
            startIcon={<Iconify icon="logos:google-icon" />}
            onClick={handleGoogleLogin}
          >
            Continue with Google
          </SocialButton>

          {loginMethod === 'otp' ? (
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
              <Typography
                onClick={() => {
                  navigate('/register');
                }}
                sx={{
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                Don't have an account?
              </Typography>
              <Typography
                onClick={() => {
                  navigate('/register');
                }}
                sx={{
                  fontSize: '0.875rem',
                  color: '#0071e3',
                  cursor: 'pointer',
                  textAlign: 'center',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Get Started
              </Typography>
            </Stack>
          ) : (
            <Typography
              onClick={() => {
                setLoginMethod('otp');
                setFailedAttempts(0);
                setTurnstileToken('');
              }}
              sx={{
                fontSize: '0.875rem',
                color: '#0071e3',
                cursor: 'pointer',
                textAlign: 'center',
                mt: 2,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in with Email Code
            </Typography>
          )}
        </>
      )}
    </FormProvider>
  );
}
